import React, { useCallback, useMemo } from 'react';
import type { FC } from 'react';
import { Alert, Badge, Box, Text, useAsync } from '@wix/cli-ui-kit';
import { match } from 'variant';
import { updateEnvFile, writeJson } from '@wix/cli-fs';
import { getWixConfigFilePath } from '@wix/cli-core-definitions';
import { getEnvFilePath } from '@wix/cli-astro-definitions';
import { extend } from '@wix/cli-project-extender';
import type { IPackageManager } from '@wix/package-manager';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { Task, TaskStatus } from '../../task-runner.js';
import { useTaskRunner } from '../../task-runner.js';
import { TaskList } from '../TaskList.js';
import type { ProjectData } from '../../hooks/create-project.js';

const GenerationProgress: FC<{
  tasks: TaskStatus[];
  totalTaskCount: number;
}> = ({ tasks, totalTaskCount }) => {
  return (
    <>
      <Box marginBottom={1}>
        <Text>Generating your headless site...</Text>
      </Box>

      <TaskList tasks={tasks} totalTaskCount={totalTaskCount} />
    </>
  );
};

export const GenerateProject: FC<{
  projectFolder: string;
  businessId: string;
  projectData: ProjectData;
  onDone: (isDone: boolean) => void;
  packageManager: IPackageManager;
}> = ({ projectFolder, businessId, projectData, onDone, packageManager }) => {
  const { execute: executeTaskRunner, status: taskRunnerStatus } =
    useTaskRunner();

  const generateProject = useCallback(async () => {
    const wixConfigFilePath = getWixConfigFilePath(projectFolder);
    const envFilePath = getEnvFilePath(projectFolder);

    await writeJson(
      wixConfigFilePath,
      {
        appId: projectData.projectId,
        siteId: businessId,
      },
      { spaces: 2 }
    );

    await updateEnvFile(envFilePath, projectData.environmentVariables);
  }, [businessId, projectFolder, projectData]);

  const extendProject = useCallback(async () => {
    await extend(projectFolder);
  }, [projectFolder]);

  const installDependencies = useCallback(async () => {
    try {
      await packageManager.runInstall(projectFolder);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToInstallDependencies(),
        cause: e,
      });
    }
  }, [projectFolder, packageManager]);

  const tasks = useMemo(() => {
    const result = [
      {
        action: generateProject,
        loadingText: 'Generating project...',
        successText: 'Project generated.',
      },
      {
        action: extendProject,
        loadingText: 'Extending project template...',
        successText: 'The project template has been successfully extended',
      },
      {
        action: installDependencies,
        loadingText: 'Installing dependencies...',
        successText: 'Dependencies installed.',
      },
    ] satisfies Task[];

    return result;
  }, [generateProject, extendProject, installDependencies]);

  useAsync(async () => {
    await executeTaskRunner(tasks);
    onDone(true);
  }, []);

  return match(taskRunnerStatus, {
    Idle: () => null,
    Running: (status) => <GenerationProgress {...status} />,
    Done: (status) => (
      <Box flexDirection="column" marginBottom={1}>
        <GenerationProgress {...status} />
        <Box marginLeft={-2} marginTop={1}>
          <Alert type="success">
            <Badge skin="success">Success</Badge> <Text>Project set up.</Text>
          </Alert>
        </Box>
      </Box>
    ),
  });
};
